import { useEffect, useState } from "react";
import api, { fmtNZD } from "@/lib/api";
import { QuickBadge, EstValue } from "@/components/QuickBadge";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import MiniRing from "@/components/MiniRing";
import { HomeBaseWeather } from "@/components/WeatherChipRow";

const jobLabel = c => {
  const ref = (c.reference || "").trim();
  return ref && !(c.name || "").trim().startsWith(ref) ? `${ref} ${c.name}` : c.name;
};

export default function Dashboard() {
  const nav = useNavigate();
  const [plus, setPlus] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [leaveSum, setLeaveSum] = useState(null);
  const [leavePending, setLeavePending] = useState(0);
  const { user } = useAuth();

  useEffect(() => {
    const lp = () => api.get("/leave/pending-count").then(r => setLeavePending(r.data.count)).catch(() => {});
    lp(); window.addEventListener("leave-changed", lp); window.addEventListener("focus", lp);
    return () => { window.removeEventListener("leave-changed", lp); window.removeEventListener("focus", lp); };
  }, []);

  useEffect(() => {
    api.get("/dashboard/plus").then(r => setPlus(r.data)).catch(() => setPlus(null));
    api.get("/leave-tracker/summary").then(r => setLeaveSum(r.data)).catch(() => {});
  }, []);

  if (!plus) return <div className="opacity-60 p-4" data-testid="dashboard">Loading dashboard…</div>;

  const t = plus.totals || {};
  const cards = plus.cards || [];
  const visibleCards = showAll ? cards : cards.slice(0, 6);
  const lightColor = (l) => l === "red" ? "bg-red-500" : l === "amber" ? "bg-amber-400" : l === "green" ? "bg-green-500" : "bg-neutral-600";

  const needs = [
    { key: "ts", label: "Timesheets awaiting approval", count: plus.timesheets_awaiting_count || 0, to: "/operations" },
    { key: "bills", label: "Bills awaiting review", count: plus.bills_awaiting_count || 0, to: "/operations" },
    ...(user?.role === "Admin" ? [{ key: "leave", label: "Leave requests to approve", count: leavePending, to: "/operations/leave" }] : []),
    { key: "stages", label: "Over-budget stages", count: plus.over_budget_stages_count || 0, to: "/projects" },
  ];

  // Ring values for the three summary cards.
  // Current & Forecast position rings represent "position as % of contract"
  // (profit retained of the contract value). Clamped 0..1 for the ring but values can be negative.
  const contract = Math.max(1, t.contract || 0);
  const currentRing = (t.current_position || 0) / contract;
  const forecastRing = (t.forecast_position || 0) / contract;
  const usedRing = (t.forecast_used_pct || 0) / 100;

  return (
    <div className="space-y-5" data-testid="dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl text-white">Dashboard</h1>
        <div className="flex flex-wrap gap-2" data-testid="dash-quick-actions">
          <button className="btn-silver text-sm" data-testid="qa-new-project" onClick={() => nav("/projects")}>+ New project</button>
          <button className="btn-ghost text-sm" data-testid="qa-new-bill" onClick={() => nav("/operations")}>+ New bill</button>
          <button className="btn-ghost text-sm" data-testid="qa-new-timesheet" onClick={() => nav("/my/timesheets")}>+ New timesheet</button>
          <button className="btn-ghost text-sm" data-testid="qa-open-programme" onClick={() => nav("/programme")}>Open works programme</button>
        </div>
      </div>

      {/* Slim home-base weather strip at the top (next 3 days + today). */}
      <div className="card" data-testid="dash-weather-home-base">
        <HomeBaseWeather testid="dash-weather-home-base-row"/>
      </div>

      <div className="space-y-5 min-w-0">
        <div className="space-y-5 min-w-0">
          {/* Summary cards with small ring/donut indicators */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="dash-summary">
            <SummaryCard testid="sum-current-position" label="Current position" sub="Contract − actual cost" value={fmtNZD(t.current_position)}
              ring={<MiniRing value={currentRing} label={`${Math.round(currentRing*100)}%`} testid="ring-current-position"/>} />
            <SummaryCard testid="sum-forecast-position" label="Forecast position" sub="Contract − forecast cost" value={fmtNZD(t.forecast_position)}
              ring={<MiniRing value={forecastRing} label={`${Math.round(forecastRing*100)}%`} testid="ring-forecast-position"/>} />
            <SummaryCard testid="sum-forecast-used" label="Forecast used" sub={`${fmtNZD(t.costs)} of ${fmtNZD(t.forecast_cost)}`} value={`${(t.forecast_used_pct ?? 0)}%`}
              ring={<MiniRing value={usedRing} label={`${Math.round((t.forecast_used_pct||0))}%`} testid="ring-forecast-used"/>} />
            {leaveSum && (
              <button type="button" className="card text-left" onClick={() => nav("/operations/leave")} data-testid="dash-leave-card">
                <div className="text-xs opacity-60">Leave liability</div>
                <div className="text-2xl" data-testid="dash-leave-liability">{fmtNZD(leaveSum.total_liability)}</div>
                <div className={`text-xs ${leaveSum.needs_review ? "text-amber-300" : "opacity-60"}`} data-testid="dash-leave-review">{leaveSum.needs_review} need review</div>
              </button>
            )}
          </div>

          <div className="card" data-testid="dash-needs-attention">
            <h2 className="text-silver mb-2 text-sm">Needs attention</h2>
            <ul className="divide-y divide-neutral-800">
              {needs.map(n => (
                <li key={n.key} className="flex items-center justify-between py-2">
                  <button className="text-left text-sm hover:text-silver flex-1" data-testid={`needs-${n.key}`} onClick={() => nav(n.to)}>
                    {n.label}
                  </button>
                  <span className={`badge ${n.count > 0 ? "badge-amber" : "badge-neutral"}`} data-testid={`needs-count-${n.key}`}>{n.count}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="card" data-testid="dash-projects">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-silver text-sm">Active projects</h2>
              <span className="text-xs opacity-60">{cards.length} open</span>
            </div>
            {t.estimates_count > 0 && <div className="text-xs text-sky-300 mb-2" data-testid="dash-estimates-line">Estimates (not in contract totals): <i>{fmtNZD(t.estimates)}</i> across {t.estimates_count} project{t.estimates_count === 1 ? "" : "s"}</div>}
            {cards.length === 0 ? (
              <div className="opacity-60 text-sm">No open projects.</div>
            ) : (
              <>
                <div className="hidden md:block">
                  <table className="table">
                    <thead>
                      <tr>
                        <th></th><th>Project</th><th>Client</th>
                        <th className="text-right">Contract</th>
                        <th className="text-right">Costs</th>
                        <th className="text-right">Forecast profit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleCards.map(c => (
                        <tr key={c.id} className="hover:bg-neutral-900 cursor-pointer" onClick={() => nav(`/projects/${c.id}`)} data-testid={`dashrow-${c.id}`}>
                          <td><span className={`inline-block w-2.5 h-2.5 rounded-full ${lightColor(c.traffic_light)}`} /></td>
                          <td className="text-silver">{jobLabel(c)}<QuickBadge p={c} testid={`dash-quick-${c.id}`}/></td>
                          <td className="opacity-80">{c.client || "—"}</td>
                          <td className="text-right">{c.pricing === "estimate" ? <EstValue label={c.estimate_label} testid={`dash-est-${c.id}`}/> : fmtNZD(c.current_contract_total)}</td>
                          <td className="text-right">{fmtNZD(c.costs_so_far)}</td>
                          <td className={`text-right font-bold ${c.forecast_profit < 0 ? "text-red-400" : ""}`}>{c.pricing === "estimate" ? "—" : fmtNZD(c.forecast_profit)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ul className="md:hidden divide-y divide-neutral-800">
                  {visibleCards.map(c => (
                    <li key={c.id} className="py-2 cursor-pointer" onClick={() => nav(`/projects/${c.id}`)} data-testid={`dashrow-m-${c.id}`}>
                      <div className="flex items-center gap-2">
                        <span className={`inline-block w-2.5 h-2.5 rounded-full ${lightColor(c.traffic_light)}`} />
                        <span className="text-silver flex-1 truncate">{jobLabel(c)}<QuickBadge p={c} testid={`dash-quick-m-${c.id}`}/></span>
                        <span className={`text-sm ${c.forecast_profit < 0 ? "text-red-400" : "text-silver"}`}>{c.pricing === "estimate" ? "" : fmtNZD(c.forecast_profit)}</span>
                      </div>
                      <div className="text-xs opacity-70 ml-4">{c.client || "—"} · {c.pricing === "estimate" ? <EstValue label={c.estimate_label} testid={`dash-est-m-${c.id}`}/> : fmtNZD(c.current_contract_total)}</div>
                    </li>
                  ))}
                </ul>
                {cards.length > 6 && (
                  <button data-testid="btn-show-all-projects" className="btn-ghost text-sm mt-2" onClick={() => setShowAll(s => !s)}>
                    {showAll ? "Show less" : `Show all projects (${cards.length})`}
                  </button>
                )}
              </>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, sub, value, ring, testid }) {
  return (
    <div className="card" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs text-silver">{label}</div>
          <div className="text-xs opacity-70 truncate">{sub}</div>
          <div className="text-xl font-bold text-silver mt-1">{value}</div>
        </div>
        {ring}
      </div>
    </div>
  );
}
